// src/components/ResetPassword.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import axios from 'axios';
import './Login.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";


interface VerifyTokenResponse {
  success: boolean;
  valid?: boolean;
  email?: string;
  message?: string;
}

interface ResetPasswordResponse {
  success: boolean;
  message?: string;
}

interface ResetPasswordProps {
  onBackToLogin: () => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ onBackToLogin }) => {
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [status, setStatus] = useState<'loading' | 'valid' | 'invalid' | 'submitting' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    // Get token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    
    if (!tokenParam) {
      setStatus('invalid');
      setError('No reset token provided.');
      return;
    }
    
    setToken(tokenParam);
    
    // Verify token
    const verifyToken = async () => {
      try {
        const response = await axios.post<VerifyTokenResponse>(`${API_BASE_URL}/auth/verify-reset-token`, {
          token: tokenParam
        });
        
        if (response.data.success && response.data.valid) {
          setEmail(response.data.email || '');
          setStatus('valid');
        } else {
          setStatus('invalid');
          setError(response.data.message || 'Invalid or expired reset link.');
        }
      } catch (err) {
        console.error('Token verification error:', err);
        setStatus('invalid');
        setError('Failed to verify reset link. Please try again.');
      }
    };
    
    verifyToken();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    
    setStatus('submitting');
    
    try {
      const response = await axios.post<ResetPasswordResponse>(`${API_BASE_URL}/auth/reset-password`, {
        token,
        password: newPassword
      });
      
      if (response.data.success) {
        setStatus('success');
      } else {
        setStatus('error');
        setError(response.data.message || 'Failed to reset password.');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      setStatus('error');
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
    }
  };

  if (status === 'loading') {
    return (
      <div className="login-page">
        <div className="login-background-dots" />
        <div className="login-card">
          <div className="login-logo-section">
            <div className="login-title-row">
              <img src="/logo.png" alt="CareShift Logo" className="login-logo-image" />
              <h1 className="login-title">CareShift</h1>
            </div>
            <p className="login-subtitle">Intelligent Nurse Handoff Assistant</p>
          </div>
          <div className="forgot-password-success">
            <p>Verifying reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="login-page">
        <div className="login-background-dots" />
        <div className="login-card">
          <div className="login-logo-section">
            <div className="login-title-row">
              <img src="/logo.png" alt="CareShift Logo" className="login-logo-image" />
              <h1 className="login-title">CareShift</h1>
            </div>
            <p className="login-subtitle">Intelligent Nurse Handoff Assistant</p>
          </div>
          <div className="forgot-password-success">
            <div className="success-icon-circle" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </div>
            <h3>Invalid Reset Link</h3>
            <p>{error}</p>
            <button 
              type="button" 
              onClick={onBackToLogin}
              className="login-submit-btn"
              style={{ marginTop: '1.5rem' }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="login-page">
        <div className="login-background-dots" />
        <div className="login-card">
          <div className="login-logo-section">
            <div className="login-title-row">
              <img src="/logo.png" alt="CareShift Logo" className="login-logo-image" />
              <h1 className="login-title">CareShift</h1>
            </div>
            <p className="login-subtitle">Intelligent Nurse Handoff Assistant</p>
          </div>
          <div className="forgot-password-success">
            <div className="success-icon-circle">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h3>Password Reset Successfully!</h3>
            <p>Your password has been updated. You can now sign in with your new password.</p>
            <button 
              type="button" 
              onClick={onBackToLogin}
              className="login-submit-btn"
              style={{ marginTop: '1.5rem' }}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-background-dots" />
      <div className="login-card">
        <div className="login-logo-section">
          <div className="login-title-row">
            <img src="/logo.png" alt="CareShift Logo" className="login-logo-image" />
            <h1 className="login-title">CareShift</h1>
          </div>
          <p className="login-subtitle">Intelligent Nurse Handoff Assistant</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <h3 className="forgot-password-title">Create New Password</h3>
          <p className="forgot-password-description">
            Enter a new password for <strong>{email}</strong>
          </p>
          
          <div className="login-form-group">
            <label className="login-form-label" htmlFor="newPassword">
              New Password
            </label>
            <div className="login-input-wrapper">
              <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
              </svg>
              <input 
                type={showPassword ? 'text' : 'password'}
                id="newPassword" 
                className="login-form-input" 
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <button 
                type="button" 
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                )}
              </button>
            </div>
            {newPassword && (
              <div className={`password-requirement ${newPassword.length >= 8 ? 'met' : 'unmet'}`}>
                {newPassword.length >= 8 ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    Minimum requirements met!
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    Password must be at least 8 characters
                  </>
                )}
              </div>
            )}
          </div>

          <div className="login-form-group">
            <label className="login-form-label" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <div className="login-input-wrapper">
              <svg className="login-input-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
              </svg>
              <input 
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword" 
                className="login-form-input" 
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
              <button 
                type="button" 
                className="login-password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                )}
              </button>
            </div>
            {confirmPassword && (
              <div className={`password-requirement ${confirmPassword && newPassword === confirmPassword ? 'met' : 'unmet'}`}>
                {confirmPassword && newPassword === confirmPassword ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    Passwords match!
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    Passwords must match
                  </>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="login-error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="login-submit-btn"
            disabled={status === 'submitting'}
          >
            {status === 'submitting' ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <div className="login-create-account-text" style={{ marginTop: '1.5rem' }}>
          <button 
            type="button"
            onClick={onBackToLogin}
            className="login-create-account-link"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
